<template>
  <div>
    <div v-if="somethingIsLoading">
      <TLoadingIndicatorBig />
    </div>
    <div v-else-if="!data">
      {{ error }}
    </div>

    <div
      class="rounded-3xl border-primary-500 shadow-xl"
      v-else-if="!user.hasPermission('collaboration_teamteaching').value"
    >
      <UpgradeRequired source="team-teaching" />
    </div>
    <div v-else>
      <TAlertDangerous
        v-if="collaboratorToRemove"
        :open="showConfirmation"
        :confirm-action-label="
          $t('course_settings.collaboration.remove_myself')
        "
        :title="$t('course_settings.collaboration.remove_myself')"
        :description="
          $t('course_settings.collaboration.remove_myself_description', [
            {
              id: 'collaboratorName',
              value: collaboratorToRemove.fullName,
            },
            {
              id: 'courseName',
              value: course.name,
            },
          ])
        "
        :confirm-action="removeCollaboratorConfirm"
        :cancel-action="removeCollaboratorCancel"
      />
      <TP2 class="mb-4">{{
        $t('course_settings.collaboration.description_1')
      }}</TP2>
      <div class="spacy-y-4">
        <div
          v-for="collaborator in collaborators"
          class="mb-2 flex items-center space-x-4 rounded-lg border border-neutral-500 p-2"
        >
          <div class="bg-success-50 rounded-lg p-3 text-sm text-success-500">
            <TIcon v-if="collaborator.isAdmin" name="person_admin" />
            <TIcon v-else name="person" />
          </div>
          <div class="grow text-neutral-950">
            {{ collaborator.fullName }}<br />
            <TPCaption class="">
              {{
                $t('course_settings.collaboration.member_since', [
                  {
                    id: 'date',
                    value: $formatDayShortWithYear(
                      new Date(collaborator.joinedAt),
                    ),
                  },
                ])
              }}
            </TPCaption>
          </div>
          <div v-if="!collaborator.isAdmin && !readOnly">
            <TButtonInlineSecondary @click="openEditCollaborator(collaborator)"
              ><span>{{ collaborator.roleName }}</span>
              <TIcon name="edit" class="ml-2 h-4 w-4" />
            </TButtonInlineSecondary>
          </div>
          <div v-else>
            <TP2 class="mr-4 text-primary-500">{{ collaborator.roleName }}</TP2>
          </div>
        </div>
      </div>
      <div class="mt-6 flex gap-x-2">
        <TButtonInline @click="openInviteModal()" v-if="!readOnly"
          >{{ $t('course_settings.collaboration.invite.cta') }}
        </TButtonInline>
        <TButtonInlineDangerous
          @click="removeCollaborator()"
          v-if="canLeaveCourse"
          >{{ $t('course_settings.collaboration.leave.cta') }}
        </TButtonInlineDangerous>
      </div>
    </div>
    <CourseSettingsCollaborationInviteModal
      :course="course"
      :show="showInviteModal"
      @close="closeInviteModal"
    />
    <CourseSettingsCollaborationEditCollaboratorModal
      :collaborator="collaboratorToEdit"
      v-if="collaboratorToEdit"
      :course="course"
      :show="showEditCollaborator"
      @close="closeEditCollaborator"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Course, CourseCollaborator } from "~/graphql";
import { useCourseCollaborationManagement } from "~/stores/courseCollaborationManagement";
import toast from "~/components/Toast";
import { useLicense } from "#imports";
import { useCourseCollaboratorsQuery } from "~/composables";
import { useGlobalDependencyProvider } from "@teachino/shared-util";
import { UpgradeRequired } from "@teachino/shared-upgrade";

const globalDi = useGlobalDependencyProvider()
const user = globalDi.inject('user')

const { canUseFeature } = useLicense();

const { $t, $calculateDifference } = useNuxtApp();
const props = defineProps<{ course: Course; readOnly?: boolean }>();


const courseCollaborationManagementStore = useCourseCollaborationManagement();
courseCollaborationManagementStore.init();

const { fetching, data, error, executeQuery } = useCourseCollaboratorsQuery({
  variables: {
    courseIdInput: {
      id: props.course.id,
    },
    courseInvitesQueryInput: {
      courseId: props.course.id,
    },
  },
});

const collaborators = computed(() => {
  if (!data.value || !data.value.courseCollaborators) {
    return [];
  }
  return data.value?.courseCollaborators
    .map((collaborator) => {
      return {
        ...collaborator,
        roleName: $t(
          `course_settings.collaboration.role.${collaborator.courseRole.toLowerCase()}`,
        ),
        roleDescription: $t(
          `course_settings.collaboration.role.${collaborator.courseRole.toLowerCase()}_description`,
        ),
        isAdmin: collaborator.courseRole === 'OWNER',
      };
    })
    .sort((a, b) => {
      const orderMap = {
        OWNER: 0,
        TEACHER: 1,
        CO_TEACHER: 2,
        GUEST: 3,
        STUDENT: 4,
      };
      if (orderMap[a.courseRole] < orderMap[b.courseRole]) {
        return -1;
      }
      if (orderMap[a.courseRole] > orderMap[b.courseRole]) {
        return 1;
      }
      return 0;
    });
});

const somethingIsLoading = computed(
  () => fetching.value || createInviteLoading.value,
);

const createInviteLoading = ref(false);

const showInviteModal = ref(false);

function openInviteModal() {
  showInviteModal.value = true;
}

function closeInviteModal() {
  showInviteModal.value = false;
  executeQuery();
}

const showEditCollaborator = ref(false);
const collaboratorToEdit = ref<CourseCollaborator | null>(null);

function openEditCollaborator(collaborator: CourseCollaborator) {
  collaboratorToEdit.value = collaborator;
  showEditCollaborator.value = true;
}

function closeEditCollaborator() {
  collaboratorToEdit.value = null;
  showEditCollaborator.value = false;
  executeQuery();
}

const collaboratorToRemove = ref(null);

const { getUserId } = useCurrentUser();
const canLeaveCourse = computed(() => {
  if (!data.value || !data.value.courseCollaborators) {
    return false;
  }
  collaboratorToRemove.value = data.value?.courseCollaborators.find(
    (collaborator) =>
      collaborator.courseRole !== 'OWNER' &&
      collaborator.userId === getUserId(),
  );
  if (collaboratorToRemove.value) {
    return true;
  }

  return false;
});

const showConfirmation = ref(false);
const loading = ref(false);
const removeCollaboratorMutation = useRemoveCourseCollaboratorMutation();

function removeCollaborator() {
  showConfirmation.value = true;
}

function removeCollaboratorCancel() {
  showConfirmation.value = false;
}

async function removeCollaboratorConfirm() {
  loading.value = true;
  const { error } = await removeCollaboratorMutation.executeMutation({
    removeCourseCollaboratorInput: {
      collaboratorId: collaboratorToRemove.value.id,
    },
  });
  if (error) {
    toast.error($t('generic_error'));
    loading.value = false;
  } else {
    toast.success($t('success'));
    loading.value = false;
    showConfirmation.value = false;
    navigateTo('/course');
  }
}
</script>
